/**
 * A minimal canvas strip chart for the live RSSI/SNR view — no charting library (zero
 * runtime deps, REQ-NFR-1). Draws a faint grid and a single polyline, auto-expanding the
 * value range to fit the data. Display-only, fed by telemetry.
 */
export type StripOpts = {
  min: number; // nominal low bound (expanded to fit data)
  max: number; // nominal high bound
  color: string;
  grid: string;
  ink: string;
  label: string;
  unit: string;
};

/** Nominal bounds expanded to include the data, with a little headroom. Pure. */
export function computeRange(
  nomMin: number,
  nomMax: number,
  values: readonly number[],
): { lo: number; hi: number } {
  let lo = nomMin;
  let hi = nomMax;
  for (const v of values) {
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (hi - lo < 1) hi = lo + 1;
  const pad = (hi - lo) * 0.08;
  return { lo: lo - pad, hi: hi + pad };
}

/** Map a value to a y pixel: `lo` at the bottom, `hi` at the top. Pure. */
export function projectY(v: number, lo: number, hi: number, top: number, height: number): number {
  return top + height - ((v - lo) / (hi - lo)) * height;
}

export function drawStrip(canvas: HTMLCanvasElement, values: number[], opts: StripOpts): void {
  const dpr = window.devicePixelRatio || 1;
  const cssW = canvas.clientWidth || 320;
  const cssH = canvas.clientHeight || 90;
  if (canvas.width !== Math.round(cssW * dpr) || canvas.height !== Math.round(cssH * dpr)) {
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  const padL = 6;
  const padR = 6;
  const padT = 16;
  const padB = 6;
  const w = cssW - padL - padR;
  const h = cssH - padT - padB;

  const { lo, hi } = computeRange(opts.min, opts.max, values);
  const y = (v: number): number => projectY(v, lo, hi, padT, h);

  // grid
  ctx.strokeStyle = opts.grid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i <= 3; i++) {
    const gy = Math.round(padT + (h / 3) * i) + 0.5;
    ctx.moveTo(padL, gy);
    ctx.lineTo(padL + w, gy);
  }
  ctx.stroke();

  // series
  if (values.length > 1) {
    ctx.strokeStyle = opts.color;
    ctx.lineWidth = 1.75;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    const step = w / (values.length - 1);
    values.forEach((v, i) => {
      const px = padL + step * i;
      const py = y(v);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.stroke();
  }

  // label + current value
  ctx.fillStyle = opts.ink;
  ctx.font = "700 10px 'Roboto Mono', ui-monospace, monospace";
  ctx.textBaseline = 'top';
  ctx.fillText(opts.label, padL, 3);
  const last = values.length ? values[values.length - 1]! : null;
  if (last != null) {
    const text = `${last.toFixed(1)} ${opts.unit}`;
    ctx.textAlign = 'right';
    ctx.fillText(text, padL + w, 3);
    ctx.textAlign = 'left';
  }
}
